import { SubscriptionPlan } from 'store/api/enums';
import { LicenseStatus } from 'store/app/enums';
import { CurrentSubscription } from 'store/billing/models/currentSubscription';
import { SubscriptionType } from 'store/billing/models/subscriptionType';
import { findSubscriptionPlanType } from 'utils/subscriptionPlanType';

type LicenceStatusSource = {
  status?: LicenseStatus | null;
} | null;

export const hasUnlimitedLicense = (
  userLicence?: LicenceStatusSource
): boolean => userLicence?.status === LicenseStatus.UNLIMITED;

const planTypeUuid = (
  plan: SubscriptionPlan,
  subscriptionPlanTypes?: SubscriptionType[]
): string | undefined =>
  findSubscriptionPlanType(subscriptionPlanTypes ?? [], plan)?.uuid;

/**
 * True when the account is on one of `plans` and should be feature-gated.
 * Unlimited licenses are never gated. A missing billing subscription is not
 * treated as a match (avoids `undefined == undefined` locking).
 */
export const isLockedToPlans = (
  plans: SubscriptionPlan[],
  currentSubscription?: CurrentSubscription | null,
  userLicence?: LicenceStatusSource,
  subscriptionPlanTypes?: SubscriptionType[]
): boolean => {
  if (hasUnlimitedLicense(userLicence)) {
    return false;
  }

  if (!currentSubscription) {
    return false;
  }

  if (plans.some((plan) => currentSubscription.description === plan)) {
    return true;
  }

  const typeId = currentSubscription.typeId;
  if (!typeId || !subscriptionPlanTypes?.length) {
    return false;
  }

  return plans.some((plan) => {
    const uuid = planTypeUuid(plan, subscriptionPlanTypes);
    return Boolean(uuid) && uuid === typeId;
  });
};

export const isSmallPlanLocked = (
  currentSubscription?: CurrentSubscription | null,
  userLicence?: LicenceStatusSource,
  subscriptionPlanTypes?: SubscriptionType[]
): boolean =>
  isLockedToPlans(
    [SubscriptionPlan.SMALL_PLAN],
    currentSubscription,
    userLicence,
    subscriptionPlanTypes
  );

export const isSmallOrMediumPlanLocked = (
  currentSubscription?: CurrentSubscription | null,
  userLicence?: LicenceStatusSource,
  subscriptionPlanTypes?: SubscriptionType[]
): boolean =>
  isLockedToPlans(
    [SubscriptionPlan.SMALL_PLAN, SubscriptionPlan.MEDIUM_PLAN],
    currentSubscription,
    userLicence,
    subscriptionPlanTypes
  );
